import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
// UPPDRAG-8 PR-E: dual-write reschedule admin-decision system messages.
import { sendLeagueMatchMessage } from "@/lib/chatDualWrite";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, CalendarDays } from "lucide-react";
import { format, addMinutes } from "date-fns";
import { validateCourtAvailability } from "@/lib/validateCourtAvailability";
import { sv } from "date-fns/locale";
import { toast } from "sonner";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import { buildAuditLogEntry } from "@/lib/rescheduleAuditHelpers";
import { getTeamPrimaryPlayers } from "@/lib/teamHelpers";

export default function AdminRescheduleQueue() {
  const { t } = useTranslation("admin");
  const { user } = useAuth();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["admin-reschedule-queue"],
    queryFn: async () => {
      // Get all reschedule requests where opponent accepted but admin hasn't decided
      const { data, error } = await supabase
        .from("reschedule_requests")
        .select("*")
        .eq("status", "accepted")
        .eq("admin_status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;

      if (!data || data.length === 0) return [];

      // Get match details for each request
      const matchIds = [...new Set(data.map((r) => r.match_id))];
      const { data: matches } = await supabase
        .from("league_matches")
        .select("id, round, division_id, home_team_id, away_team_id, scheduled_date, scheduled_time, league_id, leagues(match_duration_minutes)")
        .in("id", matchIds);

      const teamIds = [...new Set((matches ?? []).flatMap((m) => [m.home_team_id, m.away_team_id]))];
      // TASK-67 PR-D: dropped player1_id, player2_id from SELECT —
      // identity is sourced from team_members[role='primary']. Label
      // derives from the first 2 primaries by joined_at (matches the
      // canonical pair the trigger writes to match_results).
      // Phase A.1 cleanup: embed customer:customers join so customer-only
      // primaries also resolve their display name in the label. The
      // player1_id/player2_id synthesized fields remain user-only because
      // downstream notify code requires auth.users.id; customer email path
      // is Sprint 5 scope (already shipped — send-league-match-reminder).
      const { data: teams } = await supabase
        .from("teams")
        .select(`id, team_name, members:team_members(
          user_id, customer_id, role, joined_at,
          profile:profiles!team_members_user_id_fkey(full_name),
          customer:customers!team_members_customer_id_fkey(full_name, email)
        )`)
        .in("id", teamIds);

      type TeamMemberEmbed = {
        user_id: string | null;
        customer_id: string | null;
        role: "primary" | "reserve";
        joined_at: string;
        profile: { full_name: string | null } | null;
        customer: { full_name: string | null; email: string | null } | null;
      };
      type TeamLite = {
        id: string;
        team_name: string | null;
        members: TeamMemberEmbed[] | null;
      };
      const typedTeams = (teams ?? []) as unknown as TeamLite[];

      // First 2 primaries by joined_at. user_id-only filter for the notify
      // branch (player1_id/player2_id arrays); label-render uses both kinds.
      const teamPrimaryPair = new Map<string, [string | null, string | null]>();
      const teamPrimaryMembers = new Map<string, [TeamMemberEmbed | null, TeamMemberEmbed | null]>();
      for (const t of typedTeams) {
        const allPrimaries = getTeamPrimaryPlayers(t.members ?? []);
        teamPrimaryMembers.set(t.id, [allPrimaries[0] ?? null, allPrimaries[1] ?? null]);
        const userOnly = allPrimaries.filter((m) => m.user_id);
        teamPrimaryPair.set(t.id, [
          userOnly[0]?.user_id ?? null,
          userOnly[1]?.user_id ?? null,
        ]);
      }

      const playerIds = [
        ...new Set(
          Array.from(teamPrimaryPair.values())
            .flatMap(([a, b]) => [a, b])
            .filter((id): id is string => id !== null),
        ),
      ];
      const { data: profiles } = playerIds.length > 0
        ? await supabase
            .from("public_profiles" as any)
            .select("id, full_name")
            .in("id", playerIds)
        : { data: [] as { id: string; full_name: string | null }[] };

      const profileMap = Object.fromEntries((profiles ?? []).map((p) => [p.id, p]));
      const teamMap = Object.fromEntries(typedTeams.map((t) => {
        const [p1, p2] = teamPrimaryPair.get(t.id) ?? [null, null];
        const [m1, m2] = teamPrimaryMembers.get(t.id) ?? [null, null];
        // Phase A.1: dual-identity name resolution via embedded customer join.
        const nameOf = (m: TeamMemberEmbed | null, userIdFallback: string | null): string => {
          if (!m) return "?";
          const profileName = userIdFallback ? profileMap[userIdFallback]?.full_name : null;
          if (profileName) return profileName;
          const profileEmbed = m.profile?.full_name?.trim();
          if (profileEmbed) return profileEmbed;
          const customerName = m.customer?.full_name?.trim();
          if (customerName) return customerName;
          const customerEmail = m.customer?.email?.trim();
          return customerEmail || "?";
        };
        const n1 = nameOf(m1, p1);
        const n2 = nameOf(m2, p2);
        return [t.id, {
          ...t,
          // player1_id / player2_id intentionally user-only — downstream notify
          // requires auth.users.id. Customer-kind primaries get rendered via
          // the label below but excluded from in-app notification fan-out.
          player1_id: p1,
          player2_id: p2,
          label: t.team_name || `${n1} / ${n2}`,
        }];
      }));
      const matchMap = Object.fromEntries((matches ?? []).map((m) => [m.id, {
        ...m,
        home_team: teamMap[m.home_team_id],
        away_team: teamMap[m.away_team_id],
      }]));

      return data.map((r) => ({
        ...r,
        match: matchMap[r.match_id],
      }));
    },
  });

  const respondMutation = useMutation({
    mutationFn: async ({ requestId, matchId, approve, proposedDate, proposedTime, match, request }: any) => {
      if (!user) throw new Error(t("rescheduleQueue.notLoggedIn"));

      const adminStatus = approve ? "approved" : "denied";

      // Update reschedule request
      const { error: reqErr } = await supabase
        .from("reschedule_requests")
        .update({
          admin_status: adminStatus,
          admin_responded_by: user.id,
          admin_responded_at: new Date().toISOString(),
        })
        .eq("id", requestId);
      if (reqErr) throw reqErr;

      // PR-7.2: append audit-log entry for the admin decision. Helper
      // builds the canonical payload; RLS WITH CHECK pins
      // actor_id = auth.uid() and verifies actor_role='admin' against
      // has_role(auth.uid(), 'admin', league.organization_id).
      const auditEntry = buildAuditLogEntry({
        action: approve ? "admin_approved" : "admin_denied",
        requestId,
        actorId: user.id,
        actorRole: "admin",
        metadata: {
          proposed_date: proposedDate,
          proposed_time: proposedTime,
          court_id: (request as { proposed_court_id?: string }).proposed_court_id ?? null,
        },
      });
      await supabase.from("reschedule_audit_log" as never).insert(auditEntry as never);

      if (approve) {
        const durationMin = (match as any)?.leagues?.match_duration_minutes ?? 60;
        const courtId = (request as any)?.proposed_court_id ?? null;

        // Move the match + record fee — Sprint D dual-write.
        const feeCharged = (request as any)?.fee_amount ?? 0;
        const feeChargedMinor = Math.round(feeCharged * 100);
        const { error: matchErr } = await supabase
          .from("league_matches")
          .update({
            rescheduled_date: proposedDate,
            rescheduled_time: proposedTime,
            status: "rescheduled",
            ...(feeCharged > 0
              ? {
                  reschedule_fee_charged: feeCharged,            // legacy kr
                  reschedule_fee_charged_minor: feeChargedMinor, // öre
                  currency: "SEK",
                }
              : {}),
          } as any)
          .eq("id", matchId);
        if (matchErr) throw matchErr;

        // Auto-book court if proposed_court_id exists
        if (courtId) {
          const matchStartTime = new Date(`${proposedDate}T${proposedTime}`);
          const matchEndTime = addMinutes(matchStartTime, durationMin);

          // PR-AJ Layer B — replaces previous status='confirmed'-only check.
          // Now considers blocked + recurring_blocks too.
          const availability = await validateCourtAvailability(supabase, {
            courtId,
            startTime: matchStartTime,
            endTime: matchEndTime,
          });

          if (availability.available) {
            // Sprint D dual-write — free booking (price=0). Defensive trigger
            // would also catch this, but explicit dual-write is defense-in-depth.
            // PR-AN: user_id = the player who requested reschedule (booking
            // attribution stays with them); created_by = admin who approved.
            const { data: booking } = await supabase.from("bookings").insert({
              court_id: courtId,
              user_id: request.requested_by,
              created_by: user.id,
              start_time: matchStartTime.toISOString(),
              end_time: matchEndTime.toISOString(),
              duration_minutes: durationMin,
              status: "confirmed",
              price_per_hour: 0,
              price_per_hour_minor: 0,
              price_total: 0,
              price_total_minor: 0,
              currency: "SEK",
            }).select().single();

            if (booking) {
              await supabase
                .from("league_matches")
                .update({ booking_id: booking.id, court_id: courtId })
                .eq("id", matchId);
            }
          } else {
            // PR-AJ — surface specific reason (booking overlap, blocked, recurring_block).
            toast.warning(availability.message);
          }
        }
      }

      // System message
      const displayDate = format(new Date(proposedDate), "d MMM", { locale: sv });
      const displayTime = proposedTime.slice(0, 5);
      const msg = approve
        ? `✅ ${t("rescheduleQueue.adminApprovedTo", { date: displayDate, time: displayTime })}`
        : `❌ ${t("rescheduleQueue.adminDenied")}`;

      // UPPDRAG-8 PR-E: dual-write + structured metadata.
      await sendLeagueMatchMessage(supabase, {
        matchId,
        senderUserId: user.id,
        body: msg,
        isSystem: true,
        data: {
          type: "reschedule_admin_decision",
          decision: approve ? "approved" : "denied",
          proposed_date: proposedDate,
          proposed_time: proposedTime,
          admin_user_id: user.id,
        },
      });

      // Notify players — TASK-67 PR-D: home_team/away_team are the
      // synthesized objects from the queryFn above. Their player1_id /
      // player2_id fields are populated from the first 2 primaries by
      // joined_at (team_members), NOT direct DB reads of the dropped
      // teams.player1_id / player2_id columns.
      if (match?.home_team && match?.away_team) {
        const playerIds = [
          match.home_team.player1_id,
          match.home_team.player2_id,
          match.away_team.player1_id,
          match.away_team.player2_id,
        ].filter(Boolean);

        const body = approve
          ? t("rescheduleQueue.rescheduleApprovedBody", { date: displayDate, time: displayTime })
          : t("rescheduleQueue.rescheduleDeniedBody");

        await supabase.from("notifications").insert(
          playerIds.map((uid) => ({
            user_id: uid,
            organization_id: organization!.id,  // PR-A: org-scope per Phase 1 RLS
            title: approve ? t("rescheduleQueue.rescheduleApprovedTitle") : t("rescheduleQueue.rescheduleDeniedTitle"),
            body,
            type: "reschedule_admin_response",
            data: { match_id: matchId },
          }))
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-reschedule-queue"] });
      toast.success(t("admin:toast.responseSaved"));
    },
    onError: (err: Error) => toast.error(err.message || t("rescheduleQueue.somethingWentWrong")),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">{t("rescheduleQueue.loading")}</p>;
  if (requests.length === 0) return <p className="text-sm text-muted-foreground">{t("rescheduleQueue.noRequests")}</p>;

  return (
    <div className="space-y-3">
      {requests.map((req: any) => {
        const match = req.match;
        const displayDate = format(new Date(req.proposed_date), "d MMM yyyy", { locale: sv });
        const displayTime = req.proposed_time.slice(0, 5);

        return (
          <Card key={req.id}>
            <CardContent className="py-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  <span className="font-medium">
                    {match?.home_team?.label ?? "?"} vs {match?.away_team?.label ?? "?"}
                  </span>
                  {match?.round && <Badge variant="secondary" className="text-xs">R{match.round}</Badge>}
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                {match?.scheduled_date && (
                  <span className="line-through mr-2">
                    {format(new Date(match.scheduled_date), "d MMM", { locale: sv })}
                    {match.scheduled_time && ` kl ${match.scheduled_time.slice(0, 5)}`}
                  </span>
                )}
                → <span className="text-foreground font-medium">{displayDate} kl {displayTime}</span>
              </div>

              {(req.fee_amount ?? 0) > 0 && (
                <Badge variant="destructive" className="text-xs w-fit">
                  ⚠️ Avgift: {formatPrice(resolveMoney((req as any).fee_amount_minor, req.fee_amount, (req as any).currency) ?? { valueMinor: 0, currency: "SEK" })}
                </Badge>
              )}

              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  className="flex-1 gap-1"
                  disabled={respondMutation.isPending}
                  onClick={() =>
                    respondMutation.mutate({
                      requestId: req.id,
                      matchId: req.match_id,
                      approve: true,
                      proposedDate: req.proposed_date,
                      proposedTime: req.proposed_time,
                      match,
                      request: req,
                    })
                  }
                >
                  <Check className="h-3 w-3" /> {t("rescheduleQueue.approve")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 gap-1"
                  disabled={respondMutation.isPending}
                  onClick={() =>
                    respondMutation.mutate({
                      requestId: req.id,
                      matchId: req.match_id,
                      approve: false,
                      proposedDate: req.proposed_date,
                      proposedTime: req.proposed_time,
                      match,
                      request: req,
                    })
                  }
                >
                  <X className="h-3 w-3" /> {t("rescheduleQueue.deny")}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
